// src/pages/PostDetail/index.jsx
import React, { useState, useEffect } from 'react';
import { Layout, Typography, Space, Tag, Divider, BackTop, Spin, Alert } from 'antd';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AppHeader from '../../components/header'; // 修正路径
import AppFooter from '../../components/footer'; // 修正路径
import { getPostById } from '../../api/mockData';
import { UserOutlined, CalendarOutlined, EyeOutlined, HeartOutlined } from '@ant-design/icons';
import './index.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const PostDetail = ({ loggedInUser, onLogout }) => {
  const { id } = useParams(); // 使用 useParams 获取路由参数
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const postData = await getPostById(id);
        
        if (postData) {
          setPost(postData);
        } else {
          setError('文章不存在');
        }
      } catch (err) {
        setError('加载文章失败: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <Layout className="blog-layout">
        <AppHeader onLogout={onLogout}/>
        <Content className="post-detail-content">
          <div className="post-detail-container">
            <Spin size="large" tip="加载中..." />
          </div>
        </Content>
        <AppFooter />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout className="blog-layout">
        <AppHeader onLogout={onLogout}/>
        <Content className="post-detail-content">
          <div className="post-detail-container">
            <Alert
              message="加载失败"
              description={error}
              type="error"
              showIcon
            />
          </div>
        </Content>
        <AppFooter />
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout className="blog-layout">
        <AppHeader onLogout={onLogout}/>
        <Content className="post-detail-content">
          <div className="post-detail-container">
            <Alert
              message="文章不存在"
              description="您访问的文章可能已被删除或不存在"
              type="warning"
              showIcon
            />
          </div>
        </Content>
        <AppFooter />
      </Layout>
    );
  }

  return (
    <Layout className="blog-layout">
      <AppHeader onLogout={onLogout}/>
      
      <Content className="post-detail-content">
        <div className="post-detail-container">
          <Title level={1} className="post-detail-title">{post.title}</Title>
          
          <Space size="middle" className="post-meta">
            <Text>
              <UserOutlined /> {post.author}
            </Text>
            <Text>
              <CalendarOutlined /> {new Date(post.date).toLocaleDateString('zh-CN')}
            </Text>
            <Text>
              <EyeOutlined /> {post.views || 0} 次阅读
            </Text>
          </Space>
          
          <div className="post-tags">
            {post.tags && post.tags.map((tag, index) => (
              <Tag key={index} color="blue" className="post-tag">
                {tag}
              </Tag>
            ))}
          </div>
          
          <Divider />
          
          <div className="markdown-body">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({...props}) => <h1 style={{ color: '#1d1d1d', marginTop: '1.5em', marginBottom: '1em' }} {...props} />,
                h2: ({...props}) => <h2 style={{ color: '#333', marginTop: '1.5em', marginBottom: '0.8em' }} {...props} />,
                h3: ({...props}) => <h3 style={{ color: '#444', marginTop: '1.3em', marginBottom: '0.6em' }} {...props} />,
                p: ({...props}) => <p style={{ lineHeight: '1.8', fontSize: '16px', marginBottom: '1em' }} {...props} />,
                code: ({...props}) => <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }} {...props} />,
                pre: ({...props}) => (
                  <pre style={{ background: '#f5f5f5', padding: '16px', borderRadius: '6px', overflow: 'auto', margin: '16px 0' }} {...props} />
                ),
                a: ({...props}) => <a style={{ color: '#1890ff' }} {...props} />,
                li: ({...props}) => <li style={{ marginBottom: '4px' }} {...props} />,
                blockquote: ({...props}) => (
                  <blockquote style={{ borderLeft: '4px solid #1890ff', paddingLeft: '16px', marginLeft: '0', color: '#666' }} {...props} />
                )
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </div>
        
        <BackTop />
      </Content>
      
      <AppFooter />
    </Layout>
  );
};

export default PostDetail;